import * as Cesium from 'cesium';
import type { AmateurStation } from '../api/client';

/**
 * Phase 10 chunk 3 — amateur SatNOGS ground-stations layer.
 *
 * Renders the active SatNOGS Network roster (~2,000 stations after
 * the 90-day-inactivity filter) as small 3 px violet dots — visually
 * distinct from the Phase 3 chunk 4 curated catalog's 6 px colored
 * markers per docs/phase10.md § I row 24.
 *
 * Cap at MAX_VISIBLE per § II row 6 ("2,000 visible stations at any
 * time") — even if the server returns more, the client trims to
 * defend the rendering budget.
 *
 * Click → dispatches a window-level `amateur-station-pick-info`
 * event the {@see SatAmateurStationTooltip} listens for.
 */
export const AMATEUR_STATIONS_MAX_VISIBLE = 2_000;
export const AMATEUR_STATIONS_COLOR = '#b366ff';
export const AMATEUR_STATIONS_PIXEL_SIZE = 3;

export class AmateurStationsLayer {
  private readonly collection: Cesium.PointPrimitiveCollection;
  private records: AmateurStation[] = [];
  private visible = false;

  constructor(private readonly scene: Cesium.Scene) {
    this.collection = scene.primitives.add(new Cesium.PointPrimitiveCollection());
    this.collection.show = false;
  }

  /** Returns the loaded roster (read-only) — for tests + debug overlays. */
  getRecords(): readonly AmateurStation[] {
    return this.records;
  }

  /** Replace the rendered set. Trims to {@link AMATEUR_STATIONS_MAX_VISIBLE}. */
  setStations(stations: readonly AmateurStation[]): void {
    this.records = stations.slice(0, AMATEUR_STATIONS_MAX_VISIBLE);
    this.collection.removeAll();
    const violet = Cesium.Color.fromCssColorString(AMATEUR_STATIONS_COLOR);
    for (const s of this.records) {
      if (!Number.isFinite(s.latitude_deg) || !Number.isFinite(s.longitude_deg)) continue;
      this.collection.add({
        position: Cesium.Cartesian3.fromDegrees(
          s.longitude_deg,
          s.latitude_deg,
          s.altitude_m ?? 0,
        ),
        color:        violet,
        pixelSize:    AMATEUR_STATIONS_PIXEL_SIZE,
        outlineColor: Cesium.Color.BLACK.withAlpha(0.5),
        outlineWidth: 0.5,
        id: {
          kind: 'amateur-station',
          stationId:   s.id,
          name:        s.name,
          detailsUrl:  s.details_url,
          qfLocator:   s.qf_locator,
        },
      });
    }
    if (this.visible) this.collection.show = true;
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.collection.show = visible && this.records.length > 0;
  }

  destroy(): void {
    if (!this.scene.isDestroyed()) {
      this.scene.primitives.remove(this.collection);
    }
  }
}

/**
 * Pick payload shape recognized by {@link SelectionController}'s
 * Phase 10 chunk 3 router.
 */
export interface AmateurStationPick {
  kind: 'amateur-station';
  stationId:  string;
  name:       string;
  detailsUrl: string;
  qfLocator:  string | null;
}
